import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card } from '../../components/ui/card'
import { useToast } from '../../hooks/use-toast'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { supabase } from '../../lib/supabase'
import { TOTPEnrollment } from '../../components/auth/totp-enrollment'
import Background from '../../components/background'
import { SetupPassword } from 'src/components/auth/setup-password'
import { processAuthLink, cleanupAuthLinkUrl, getRedirectUrlBasedOnRole } from '../../lib/auth-link-utils'
import { getUserProfile } from '../../lib/api/api'
import { env } from '../../lib/env'

export const steps: string[] = ['Setup Password', 'Setup 2FA', 'Finish']

export function CompleteInvitePage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showTOTP, setShowTOTP] = useState(false)
  const [hasAccessToken, setHasAccessToken] = useState(false)
  const [redirectTo, setRedirectTo] = useState('/admin/dashboard')
  const navigate = useNavigate()
  const { toast } = useToast()

  // Handle auth link processing when component mounts
  useEffect(() => {
    const handleAuthFlow = async () => {
      console.log('[CompleteInvitePage] useEffect - handleAuthFlow called');
      setIsLoading(true); // Set loading while processing the link

      try {
        // Pass `location` from `window.location`
        const result = await processAuthLink(supabase, window.location, 'invite');

        if (result.success && result.session) {
          console.log('[CompleteInvitePage] Successfully authenticated via processAuthLink. Session:', result.session);
          setHasAccessToken(true);
          cleanupAuthLinkUrl(window.location, false); // Clear hash and 'code' param
        } else {
          console.log('[CompleteInvitePage] Authentication failed via processAuthLink. Error:', result.error);
          toast({
            title: 'Error',
            description: result.error?.message || 'Invalid or expired invite link. Please request a new invitation.',
            variant: 'destructive',
          });
          navigate('/auth/login', { replace: true });
        }
      } catch (error) { // Catch unexpected errors from processAuthLink
        console.error('[CompleteInvitePage] Unexpected error in handleAuthFlow:', error);
        toast({
          title: 'Error',
          description: 'Failed to process invite link. Please try again.',
          variant: 'destructive',
        });
        navigate('/auth/login', { replace: true });
      } finally {
        setIsLoading(false); // Clear loading state
      }
    };

    handleAuthFlow();
  }, [navigate, toast, supabase]) // Added supabase to dependencies

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    if (!hasAccessToken) {
      setFormError('Invalid session. Please use a valid invite link.')
      return
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setFormError('Passwords do not match')
      return
    }

    // Validate password length
    if (password.length < 12) {
      setFormError('Password must be at least 12 characters long')
      return
    }

    setIsLoading(true)
    try {
      // Get the current session to verify it's still valid
      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Your session has expired. Please use a new invite link.')
      }

      // Update the password
      const { error } = await supabase.auth.updateUser({ password })

      if (error) {
        throw error
      }

      toast({
        title: 'Password Set',
        description: 'Password has been set successfully. Please complete the security setup by configuring two-factor authentication.',
      })

      // Get user profile to check role
      const userProfile = await getUserProfile()

      // Determine redirect URL using the utility function
      const newRedirectTo = getRedirectUrlBasedOnRole(userProfile, env)

      // All users must complete TOTP enrollment
      setRedirectTo(newRedirectTo)
      setShowTOTP(true)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to set password'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })

      // If we get a session error, redirect to login
      if (error.message?.includes('session')) {
        navigate('/auth/login')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Background>
      <div className={`flex min-h-screen items-center justify-center`}>
        {!showTOTP ? (
          <>
            {!hasAccessToken && !isLoading ? (
              <Card className="w-full max-w-lg border border-white rounded-3xl bg-white/50 p-8">
                <Alert>
                  <AlertDescription>Invalid invite link</AlertDescription>
                </Alert>
              </Card>
            ) : (
              <SetupPassword
                setPassword={setPassword}
                setConfirmPassword={setConfirmPassword}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
                formError={formError}
              />
            )}
          </>
        ) : (
          <TOTPEnrollment />
        )}
      </div>
    </Background>
  )
}
